/**
 * screens/InboundRulesScreen.tsx — Inbound Rules (Inbound group).
 * After-hours routing, capacity, greeting, behaviour & compliance (spam
 * auto-block → DND incoming, record/transcribe DPDP). Wired to
 * /v1/inbound/settings.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, Switch } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { Card, Button, ScreenBody } from '../components/voiceUi';
import { getInboundSettings, updateInboundSettings } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

export default function InboundRulesScreen() {
  const { theme } = useTheme(); const c = theme.colors;
  const { role, orgId } = useConsole();
  const canWrite = role !== 'viewer';

  const [s, setS] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await getInboundSettings();
    setS(data ?? { afterHoursAction: 'voicemail', transferNumber: '', maxConcurrentInbound: 10, overflowVoicemailThreshold: 8, greetingLanguage: 'auto', offerLiveIntentsOnly: false, autoBlockSpam: true, recordTranscribe: true, dpdpConsentLine: true });
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load, orgId]);

  const set = (k: string, v: any) => setS((p: any) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      // after_hours_action / transfer_number are intentionally NOT sent — that
      // routing lives in Agent Settings → Hours (the source the runtime reads).
      await updateInboundSettings({
        max_concurrent_inbound: Number(s.maxConcurrentInbound), overflow_voicemail_threshold: Number(s.overflowVoicemailThreshold),
        greeting_language: s.greetingLanguage, offer_live_intents_only: s.offerLiveIntentsOnly,
        auto_block_spam: s.autoBlockSpam, record_transcribe: s.recordTranscribe, dpdp_consent_line: s.dpdpConsentLine,
      });
      showSuccess(null, 'Inbound rules saved');
    } catch (e) { handleApiError(e, 'Action failed'); } finally { setBusy(false); }
  };

  if (loading || !s) return <ScreenBody><ActivityIndicator color={c.ink3} /></ScreenBody>;

  return (
    <ScreenBody maxWidth={720}>
      <Text style={{ fontSize: 12.5, color: c.ink3, lineHeight: 19, marginBottom: 16, maxWidth: 660 }}>
        How SK Voice answers incoming calls — after-hours routing, capacity, greeting language and compliance. Blocked callers (DND · Incoming) and the agent persona are managed on their own screens; this page cross-references them rather than duplicating.
      </Text>
      {/* After-hours routing is configured on the Agent Settings → Hours screen
          (the single source the call runtime actually reads). This page used to
          duplicate it into a separate table that was never read — a dead control.
          We now cross-reference the real place instead of duplicating it. */}
      <Card style={{ padding: 16, marginBottom: 12 }}>
        <Text style={{ fontSize: 14, fontWeight: '700', color: c.ink, marginBottom: 6 }}>After-hours routing</Text>
        <Text style={{ fontSize: 12.5, color: c.ink3, lineHeight: 19 }}>
          What the agent does when the venue is closed (voicemail, forward, or answer anyway) is set on <Text style={{ fontWeight: '700', color: c.ink2 }}>Agent Settings → Hours</Text>, alongside the business hours it depends on. Managing it in one place keeps the hours and the after-hours action from drifting apart.
        </Text>
      </Card>

      <Card style={{ padding: 16, marginBottom: 12 }}>
        <Text style={{ fontSize: 14, fontWeight: '700', color: c.ink, marginBottom: 10 }}>Capacity & greeting</Text>
        <TextRow label="Max concurrent inbound" value={String(s.maxConcurrentInbound)} onChange={(v) => set('maxConcurrentInbound', v)} editable={canWrite} numeric />
        <TextRow label="Overflow → voicemail threshold" value={String(s.overflowVoicemailThreshold)} onChange={(v) => set('overflowVoicemailThreshold', v)} editable={canWrite} numeric />
        <TextRow label="Greeting language" value={s.greetingLanguage} onChange={(v) => set('greetingLanguage', v)} editable={canWrite} />
      </Card>

      <Card style={{ padding: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: '700', color: c.ink, marginBottom: 10 }}>Behaviour & compliance</Text>
        <ToggleRow label="Offer live intents only" value={s.offerLiveIntentsOnly} onChange={(v) => set('offerLiveIntentsOnly', v)} editable={canWrite} />
        <ToggleRow label="Auto-block flagged spam → DND incoming" value={s.autoBlockSpam} onChange={(v) => set('autoBlockSpam', v)} editable={canWrite} />
        <ToggleRow label="Record & transcribe" value={s.recordTranscribe} onChange={(v) => set('recordTranscribe', v)} editable={canWrite} />
        <ToggleRow label="DPDP consent line" value={s.dpdpConsentLine} onChange={(v) => set('dpdpConsentLine', v)} editable={canWrite} />
        {canWrite && <View style={{ marginTop: 14 }}><Button kind="primary" label={busy ? 'Saving…' : 'Save'} onPress={save} /></View>}
      </Card>
    </ScreenBody>
  );
}

function TextRow({ label, value, onChange, editable, numeric, mono }: { label: string; value: string; onChange: (v: string) => void; editable: boolean; numeric?: boolean; mono?: boolean }) {
  const { theme } = useTheme(); const c = theme.colors; const { TextInput } = require('react-native');
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 10 }}>
      <Text style={{ flex: 1, color: c.ink2, fontSize: 13 }}>{label}</Text>
      <TextInput value={value} onChangeText={onChange} editable={editable} keyboardType={numeric ? 'numeric' : 'default'}
        style={{ width: 160, height: 34, paddingHorizontal: 10, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: editable ? c.card : c.paper2, fontFamily: mono ? theme.font.mono : theme.font.sans, fontSize: 13 }} />
    </View>
  );
}
function ToggleRow({ label, value, onChange, editable }: { label: string; value: boolean; onChange: (v: boolean) => void; editable: boolean }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 10 }}>
      <Text style={{ flex: 1, color: c.ink2, fontSize: 13 }}>{label}</Text>
      <Switch value={value} onValueChange={onChange} disabled={!editable} />
    </View>
  );
}
